import { ORPCError } from "@orpc/client";
import {
  type AudioUploadInput,
  audioUploadCreateSchema,
} from "@rosc/contracts";
import {
  completeAudioUpload,
  createAudioUpload,
  putAudioUpload,
} from "@/services/audio-uploads";

export const prepareOwnedAudioFile = async (
  file: Blob,
  signal?: AbortSignal,
) => {
  signal?.throwIfAborted();
  if (
    file.type === "audio/webm;codecs=opus" ||
    file.type === "audio/webm; codecs=opus"
  )
    file = file.slice(0, file.size, "audio/webm");
  // Chrome labels .m4a files audio/mp4: same bytes as audio/x-m4a.
  else if (file.type === "audio/mp4")
    file = file.slice(0, file.size, "audio/x-m4a");
  const metadata = audioUploadCreateSchema
    .omit({ checksumSha256: true })
    .parse({ contentType: file.type, byteLength: file.size });
  const bytes = await file.arrayBuffer();
  signal?.throwIfAborted();
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  signal?.throwIfAborted();
  return {
    file,
    input: {
      ...metadata,
      checksumSha256: btoa(String.fromCharCode(...digest)),
    },
  };
};
export const uploadOwnedAudioFile = async ({
  file,
  input,
  identity,
  uploadId,
  onCreated,
  isCurrent,
  onProgress,
}: {
  file: Blob;
  input: AudioUploadInput;
  identity: { key: string; userUid: string; signal: AbortSignal };
  uploadId?: string;
  onCreated: (id: string) => void;
  isCurrent: () => boolean;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}): Promise<string> => {
  const check = () => {
    identity.signal.throwIfAborted();
    if (!isCurrent())
      throw new DOMException("Audio upload session changed", "AbortError");
  };
  check();
  if (uploadId) {
    // A retry of an upload created earlier. Completion is idempotent (an
    // upload already completed answers 200); 409 CONFLICT here means the
    // file never reached storage (the PUT was cut off), so it is uploaded
    // again below with the same idempotency key. Expected, not an error.
    try {
      await completeAudioUpload(uploadId, input, identity);
      check();
      return uploadId;
    } catch (error) {
      check();
      if (!(error instanceof ORPCError) || error.code !== "CONFLICT")
        throw error;
    }
  }
  const created = await createAudioUpload(input, identity);
  check();
  if (uploadId && created.id !== uploadId)
    throw Error("Upload recovery identity changed");
  onCreated(created.id);
  check();
  if (created.status === "pending") {
    await putAudioUpload(created.upload, file, {
      signal: identity.signal,
      onProgress: (progress) => {
        if (!identity.signal.aborted && isCurrent()) onProgress?.(progress);
      },
    });
    check();
  }
  await completeAudioUpload(created.id, input, identity);
  check();
  return created.id;
};
